// Register — 3-step signup wizard.
//
// ════════════════════════════════════════════════════════════════════
// WHY THIS LOOKS THE WAY IT DOES (2026-05-21 rewrite)
// ════════════════════════════════════════════════════════════════════
//
// The old register page crammed every field onto a single scrollable
// form: name, email, phone, password, dob, referral, geo. On a mid-
// size phone it overflowed the visible area three times over. We
// replaced it with a 3-step wizard:
//
//   • Step 1 ("About you")  — first name, last name, date of birth
//                              (must be 18+), username.
//   • Step 2 ("Sign-in")    — email, mobile, password, referral
//                              code (optional).
//   • Step 3 ("Location")   — GPS primary, pincode + dropdown
//                              alternatives revealed via a "use a
//                              different method" toggle. Any of the
//                              three lets the player finish signup;
//                              GPS gets the visual priority because
//                              we want as many GPS captures as
//                              possible for the local-mesh router.
//
// Every field on steps 1 + 2 is validated live and the unique
// identifiers (username, email, phone) ping
// /auth/check-availability on blur. If the value collides with an
// existing account the inline error flips to a "this username is
// already taken" style message — so the player doesn't fill the
// whole wizard and then get rejected at submit.
//
// After submit the wizard auto-logs the player in and routes to /.
// Verification (email / telegram) is offered post-signup on an
// opt-in panel and can be done later from Profile.

import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '@baaligames/shared-ui/lib/api';
import { useAuth } from '@baaligames/shared-ui/lib/auth';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import Icon from '@baaligames/shared-ui/components/Icon';
import LocationCapture, {
  type GeoBag,
  EMPTY_GEO_BAG,
} from '@baaligames/shared-ui/components/LocationCapture';

type RegResult = {
  userId: number;
  referralCode?: string;
  referredBy?: string | null;
  verify: {
    email: { sent: boolean };
    telegram: { deepLink: string | null; expiresInSec: number };
  };
};

type Form = {
  first_name: string;
  last_name: string;
  birthdate: string;
  username: string;
  email: string;
  phone: string;
  password: string;
  referralCode: string;
};

type Step = 1 | 2 | 3;

const INITIAL_FORM: Form = {
  first_name: '', last_name: '', birthdate: '', username: '',
  email: '', phone: '', password: '', referralCode: '',
};

// ─── Per-field validators ─────────────────────────────────────────

function vName(v: string): string | null {
  const s = v.trim();
  if (!s) return 'register.v.required';
  if (s.length < 2) return 'register.v.nameMin';
  if (s.length > 80) return 'register.v.tooLong';
  if (!/^[A-Za-z][A-Za-z' .-]*$/.test(s)) return 'register.v.nameChars';
  return null;
}

function vBirthdate(v: string): string | null {
  if (!v) return 'register.v.required';
  const d = new Date(v);
  if (isNaN(d.getTime())) return 'register.v.invalidDate';
  const age = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
  // 2026-05-21 — Min age raised from 13 to 18. Real-money play is
  // adult-only on the platform per legal posture; the rest of the
  // app already gates on age in places (KYC, payouts) so the
  // signup gate is now consistent with downstream restrictions.
  if (age < 18)  return 'register.v.minAge';
  if (age > 110) return 'register.v.tooOld';
  return null;
}

function vUsername(v: string): string | null {
  const s = v.trim();
  if (!s) return 'register.v.required';
  if (s.length < 3)  return 'register.v.usernameMin';
  if (s.length > 24) return 'register.v.usernameMax';
  if (!/^[a-zA-Z0-9_]+$/.test(s)) return 'register.v.usernameChars';
  return null;
}

// Email is OPTIONAL (backend migration 154): empty is allowed and stored as
// NULL. A PROVIDED value must still be a well-formed, not-too-long email.
function vEmail(v: string): string | null {
  const s = v.trim();
  if (!s) return null;   // optional — blank is fine
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)) return 'register.v.emailShape';
  if (s.length > 120) return 'register.v.tooLong';
  return null;
}

function vPhone(v: string): string | null {
  if (!v) return 'register.v.required';
  if (!/^\d{10}$/.test(v)) return 'register.v.phoneDigits';
  if (!/^[6-9]/.test(v)) return 'register.v.phonePrefix';
  return null;
}

function vPassword(v: string): string | null {
  if (!v) return 'register.v.required';
  if (v.length < 8) return 'register.v.pwdMin';
  if (v.length > 72) return 'register.v.tooLong';
  if (!/[A-Za-z]/.test(v)) return 'register.v.pwdLetter';
  if (!/[0-9]/.test(v))   return 'register.v.pwdNumber';
  return null;
}

function vReferral(v: string): string | null {
  if (!v) return null;
  if (v.length < 4)  return 'register.v.referralShort';
  if (v.length > 20) return 'register.v.tooLong';
  if (!/^[A-Z0-9]+$/.test(v)) return 'register.v.referralChars';
  return null;
}

// DOB picker bounds. max = 18 years ago: enforces the 18+ age gate at the
// native control AND makes the calendar open near a plausible adult year
// instead of today (a player no longer scrolls back 18+ years). min = 110
// years ago. Built from LOCAL y/m/d to avoid the toISOString() UTC drift.
const _dobToday = new Date();
const _ymd = (y: number, m: number, d: number) =>
  `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const DOB_MAX = _ymd(_dobToday.getFullYear() - 18, _dobToday.getMonth() + 1, _dobToday.getDate());
const DOB_MIN = _ymd(_dobToday.getFullYear() - 110, _dobToday.getMonth() + 1, _dobToday.getDate());

const VALIDATORS: Record<keyof Form, (v: string) => string | null> = {
  first_name:   vName,
  last_name:    vName,
  birthdate:    vBirthdate,
  username:     vUsername,
  email:        vEmail,
  phone:        vPhone,
  password:     vPassword,
  referralCode: vReferral,
};

const STEP_FIELDS: Record<Step, Array<keyof Form>> = {
  1: ['first_name', 'last_name', 'birthdate', 'username'],
  2: ['email', 'phone', 'password'],
  3: [],
};

// ─── Availability check ──────────────────────────────────────────
//
// Calls POST /auth/check-availability for username / email / phone.
// Debounced, value-keyed so consecutive identical lookups are
// served from local memory and don't re-hit the rate-limited route.

type AvailField = 'username' | 'email' | 'phone';
type AvailState = 'idle' | 'checking' | 'available' | 'taken';

const AVAIL_API_FIELD: Record<AvailField, AvailField> = {
  username: 'username', email: 'email', phone: 'phone',
};

type AvailMap = Record<AvailField, { value: string; state: AvailState }>;
const INITIAL_AVAIL: AvailMap = {
  username: { value: '', state: 'idle' },
  email:    { value: '', state: 'idle' },
  phone:    { value: '', state: 'idle' },
};

// ─── Field input — shared chrome ─────────────────────────────────

function Field({
  label, hint, error, optional, hintRight, children,
}: {
  label: string;
  hint?: string;
  hintRight?: React.ReactNode;
  error?: string | null;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label className="label">
          {label}
          {optional && (
            <span className="text-xs opacity-70 font-normal ml-1">(optional)</span>
          )}
        </label>
        {hintRight}
      </div>
      {children}
      {error
        ? <p className="auth-error mt-1">{error}</p>
        : hint && <p className="auth-field-help mt-1">{hint}</p>}
    </div>
  );
}

// Status badge shown to the right of unique-identifier labels.
function AvailBadge({ state }: { state: AvailState }) {
  if (state === 'idle') return null;
  if (state === 'checking') {
    return (
      <span className="text-[10.5px] inline-flex items-center gap-1 text-arena-500 dark:text-neon-200/60">
        <Icon name="progress_activity" size={11} className="animate-spin" />
        checking…
      </span>
    );
  }
  if (state === 'available') {
    return (
      <span className="text-[10.5px] inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-300 font-semibold">
        <Icon name="check_circle" size={11} filled /> available
      </span>
    );
  }
  return (
    <span className="text-[10.5px] inline-flex items-center gap-1 text-rose-600 dark:text-rose-300 font-semibold">
      <Icon name="error" size={11} filled /> already taken
    </span>
  );
}

// ─── Stepper ──────────────────────────────────────────────────────

function Stepper({ step, labels }: { step: Step; labels: Record<Step, string> }) {
  return (
    <div className="mb-3">
      <div className="flex items-center gap-1.5 mb-1">
        {[1, 2, 3].map((n) => (
          <div
            key={n}
            className={`flex-1 h-1.5 rounded-full transition
              ${step >= (n as Step)
                ? 'bg-gradient-to-r from-brand-400 to-brand-600'
                : 'bg-arena-200 dark:bg-arena-700/60'}`}
          />
        ))}
      </div>
      <div className="text-[11px] tracking-[.22em] uppercase font-bold text-arena-500 dark:text-neon-200/70 text-center">
        Step {step} of 3 · {labels[step]}
      </div>
    </div>
  );
}

// ─── Location section ────────────────────────────────────────────
//
// 2026-05-21 — GPS-first layout. The big primary CTA is the
// "Use my current location" button. The pincode + state/district
// drop-downs sit behind a "use a different method" toggle. The
// player can finish signup with ANY of the three captured — the
// previous "must lock GPS" gate is gone (Universal Data Saving rule:
// take whichever signal the player gives us, the locator handles
// the rest; the GeoForcedModal can ask for GPS later inside the
// app if needed).

function hasAnyGeo(geo: GeoBag): boolean {
  return (
    (geo.latitude != null && geo.longitude != null) ||
    geo.pincode_id != null ||
    geo.area_id != null ||
    geo.district_id != null ||
    geo.state_id != null
  );
}

// ─── Main component ──────────────────────────────────────────────

export default function Register() {
  const t = useT();
  const nav = useNavigate();
  const { login, refreshMe } = useAuth();

  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<Form>(INITIAL_FORM);
  const [geo, setGeo]   = useState<GeoBag>(EMPTY_GEO_BAG);
  const [touched, setTouched] = useState<Partial<Record<keyof Form, true>>>({});
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErr, setFieldErr] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState<RegResult | null>(null);
  const [autoLoginOk, setAutoLoginOk] = useState(false);
  const [avail, setAvail] = useState<AvailMap>(INITIAL_AVAIL);
  // Toggle for the alternative-location sub-panel (pincode + drop-downs).
  // Closed by default so the visual priority lands on the GPS button.
  const [showAltGeo, setShowAltGeo] = useState(false);

  // Per-field live error map (computed each render).
  const errors = useMemo(() => {
    const out: Partial<Record<keyof Form, string>> = {};
    for (const k of Object.keys(VALIDATORS) as (keyof Form)[]) {
      const e = VALIDATORS[k](form[k]);
      if (e) out[k] = e;
    }
    return out;
  }, [form]);

  // ── Availability checker (debounced, value-keyed) ──────────────
  //
  // On blur of username/email/phone (and on a 600ms idle after the
  // last keystroke for users who never blur), kick a single API
  // call. Skips entirely if the shape is invalid (we'd get a 400)
  // or if the value matches the last successfully-checked one.
  const lastCheckedRef = useRef<Record<AvailField, string>>({
    username: '', email: '', phone: '',
  });

  async function checkAvailability(field: AvailField, raw: string) {
    // Normalize the same way the backend does so cache hits are
    // consistent (lowercase email; phone with +91 prefix; trimmed).
    let value = raw.trim();
    if (field === 'email') value = value.toLowerCase();
    if (field === 'phone') value = '+91' + value;
    // Cheap shape pre-check — match the backend's regex so we don't
    // burn a rate-limit slot on a value that will 400 anyway.
    const ok =
      field === 'username' ? /^[a-zA-Z0-9_]{3,30}$/.test(value)
      : field === 'email'  ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      : /^\+?\d{7,15}$/.test(value);
    if (!ok) {
      setAvail((m) => ({ ...m, [field]: { value: raw, state: 'idle' } }));
      return;
    }
    if (lastCheckedRef.current[field] === value) return; // already checked
    setAvail((m) => ({ ...m, [field]: { value: raw, state: 'checking' } }));
    try {
      const r = await api.post<{ available: boolean }>(
        '/auth/check-availability',
        { field: AVAIL_API_FIELD[field], value },
      );
      lastCheckedRef.current[field] = value;
      setAvail((m) => ({
        ...m,
        [field]: { value: raw, state: r.available ? 'available' : 'taken' },
      }));
    } catch {
      // Network / rate-limit error — fall back to idle so submit can
      // still try (server is the source of truth).
      setAvail((m) => ({ ...m, [field]: { value: raw, state: 'idle' } }));
    }
  }

  // Debounced auto-check for users who keep typing without blurring.
  useEffect(() => {
    const tm = window.setTimeout(() => {
      if (form.username && !errors.username) checkAvailability('username', form.username);
      if (form.email    && !errors.email)    checkAvailability('email',    form.email);
      if (form.phone    && !errors.phone)    checkAvailability('phone',    form.phone);
    }, 600);
    return () => window.clearTimeout(tm);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.username, form.email, form.phone]);

  const trimmedRefCode = form.referralCode.trim();
  const geoOk = hasAnyGeo(geo);
  // 2026-05-21 — Signup now accepts ANY captured geo (GPS, pincode,
  // state/district/area cascade). The hard-block on "must lock GPS"
  // is gone — Universal Data Saving rule. If the player skips geo
  // entirely AND has no referral code, the server-side G1 gate kicks
  // in and rejects with a friendly message; we surface that as a
  // step-3 error rather than locking the button.
  const isUniqueOk = (f: AvailField) => avail[f].state !== 'taken';
  const stepValid: Record<Step, boolean> = {
    1: STEP_FIELDS[1].every((k) => !errors[k]) && isUniqueOk('username'),
    2: STEP_FIELDS[2].every((k) => !errors[k]) && !errors.referralCode
       && isUniqueOk('email') && isUniqueOk('phone'),
    3: geoOk || !!trimmedRefCode,
  };

  function update<K extends keyof Form>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
    setTouched((t) => ({ ...t, [key]: true }));
    setFieldErr((fe) => { const { [key]: _, ...rest } = fe; return rest; });
  }

  function markStepTouched(s: Step) {
    const next: Partial<Record<keyof Form, true>> = { ...touched };
    for (const k of STEP_FIELDS[s]) next[k] = true;
    setTouched(next);
  }

  function showErr(k: keyof Form): string | undefined {
    // Availability wins over shape error (it's the more specific
    // reason) — if email is shape-valid but already taken, the
    // collision message is what helps the player.
    if (k === 'username' && avail.username.state === 'taken') return t('register.v.usernameTaken');
    if (k === 'email'    && avail.email.state    === 'taken') return t('register.v.emailTaken');
    if (k === 'phone'    && avail.phone.state    === 'taken') return t('register.v.phoneTaken');
    if (fieldErr[k]) return fieldErr[k];
    if (touched[k] && errors[k]) return t(errors[k]);
    return undefined;
  }

  function next() {
    markStepTouched(step);
    if (!stepValid[step]) return;
    setError(null);
    if (step < 3) setStep(((step as number) + 1) as Step);
  }

  function back() {
    setError(null);
    if (step > 1) setStep(((step as number) - 1) as Step);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    markStepTouched(3);
    if (!stepValid[3]) {
      setError(t('register.v.noLocation'));
      return;
    }
    setError(null); setFieldErr({}); setLoading(true);
    try {
      const payload: Record<string, string | number | null> = {
        // Email is optional (migration 154) — send null when blank.
        email:      form.email.trim() ? form.email.trim().toLowerCase() : null,
        username:   form.username.trim(),
        phone:      '+91' + form.phone,
        password:   form.password,
        first_name: form.first_name.trim(),
        last_name:  form.last_name.trim(),
        birthdate:  form.birthdate,
      };
      if (trimmedRefCode) payload.referralCode = trimmedRefCode.toUpperCase();
      if (geo.latitude    != null) payload.latitude    = geo.latitude;
      if (geo.longitude   != null) payload.longitude   = geo.longitude;
      if (geo.state_id    != null) payload.state_id    = geo.state_id;
      if (geo.district_id != null) payload.district_id = geo.district_id;
      if (geo.area_id     != null) payload.area_id     = geo.area_id;
      if (geo.pincode_id  != null) payload.pincode_id  = geo.pincode_id;
      if (geo.nation)              payload.nation      = geo.nation;

      const r = await api.post<RegResult>('/auth/register', payload);
      setDone(r);

      try {
        // Auto-login by username (always present) — email is optional now,
        // so it can't be the login identifier.
        await login(form.username.trim(), form.password);
        await refreshMe();
        setAutoLoginOk(true);
      } catch (loginErr) {
        // Auto-login can fail if /auth/login is rate-limited right
        // after the register call, or if the session cookie didn't
        // round-trip cleanly. We surface this to the welcome panel so
        // the "Continue to the app" button routes to /login (where
        // the player can sign in manually) instead of dropping them
        // on / without auth — that path renders the home shell with
        // no BottomNav because the auth gate fails silently.
        console.error('[register] auto-login failed', loginErr);
        setAutoLoginOk(false);
      }
    } catch (e) {
      const err = e as ApiError;
      if (err.code === 'validation_error' && err.details) {
        const fe = (err.details as { fieldErrors?: Record<string, string[]> }).fieldErrors || {};
        const flat: Record<string, string> = {};
        for (const k in fe) flat[k] = fe[k][0];
        setFieldErr(flat);
        const firstBad = Object.keys(fe)[0] as keyof Form | undefined;
        if (firstBad) {
          const stepWithIt = (Object.keys(STEP_FIELDS) as unknown as Step[]).find(
            (s) => STEP_FIELDS[s].includes(firstBad as keyof Form),
          );
          if (stepWithIt && stepWithIt !== step) setStep(stepWithIt);
        }
      } else {
        setError(err.message);
      }
    } finally { setLoading(false); }
  }

  // ─── Post-signup verify panel ───────────────────────────────────
  if (done) {
    const tg = done.verify.telegram.deepLink;
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <div className="auth-head">
            <div className="auth-head-badge">
              <Icon name="check_circle" size={22} filled />
            </div>
            <h1 className="auth-h1">{t('register.welcome.title')}</h1>
            <p className="auth-sub">{t('register.welcome.subtitle')}</p>
          </div>

          {/* Welcome-screen buttons. The shared `.auth-cta` class is
              `uppercase + letter-spacing .14em` which is right for
              big "Create Account" type CTAs but wrong here — the
              wide letter-spacing on "VERIFY VIA TELEGRAM" /
              "टेलीग्राम से सत्यापित करें" stretched the label across
              the full button width, leaving the flanking bolt + open-
              in-new icons stranded at the button's left and right
              edges. Looked spread out, unbalanced.
              Fix: inline-override the text transform + letter-spacing,
              switch the wrappers themselves to flex (so the content
              is properly centered as a tight group), and size the
              icons explicitly so they don't visually compete with the
              label. The Continue-to-app arrow also moved to the right
              side of its label — for a forward action it reads
              naturally as "Continue →" rather than "← Continue". */}
          {/* 2026-05-30 — Eye-catching warning before the Telegram CTA.
              SMTP is unprovisioned so email verification is effectively
              dead today; Telegram is the only path. Users who skip
              verification CANNOT self-serve a forgotten-password reset
              — only the admin/agent can help. The verifyExplain copy
              below repeats the consequence in the user's language. */}
          <div className="mb-3 px-3 py-2.5 rounded-xl border-2 border-amber-400/60 bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-200 text-[12px] leading-snug">
            <div className="font-bold mb-0.5 inline-flex items-center gap-1">
              <Icon name="warning" size={14} filled /> {t('register.welcome.verifyHeads')}
            </div>
            <div>{t('register.welcome.verifyConsequence')}</div>
          </div>

          {tg && (
            <a
              href={tg} target="_blank" rel="noreferrer"
              className="auth-cta w-full flex items-center justify-center gap-2"
              style={{ textTransform: 'none', letterSpacing: '.01em', fontSize: '0.95rem' }}
            >
              <Icon name="bolt" filled size={18} />
              <span>{t('register.welcome.verifyTelegram')}</span>
              <Icon name="open_in_new" size={14} />
            </a>
          )}

          <p className="text-[11.5px] text-arena-500 dark:text-neon-200/65 mt-2 leading-snug text-center px-2">
            {t('register.welcome.verifyExplain')}
          </p>

          {/* If auto-login succeeded the session cookie is set and we
              route straight into the app at /. If it failed (rate
              limit, cookie hiccup, etc.) we route to /login instead
              so the player can sign in manually — landing on / with
              no auth would render the home shell without BottomNav
              and trap them in a half-logged-in dead end. */}
          <button
            type="button"
            onClick={() =>
              nav(autoLoginOk ? '/' : '/login', { replace: true })
            }
            className="auth-cta-ghost w-full mt-3 flex items-center justify-center gap-2"
            style={{ textTransform: 'none', letterSpacing: '.01em', fontSize: '0.95rem' }}
          >
            <span>
              {autoLoginOk
                ? t('register.welcome.continue')
                : t('register.welcome.continueLogin')}
            </span>
            <Icon name="arrow_forward" size={18} />
          </button>

          <div className="auth-foot text-center">
            <Link to="/login" className="auth-link text-xs">
              {t('register.welcome.useDifferent')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const stepLabels: Record<Step, string> = {
    1: t('register.step.about'),
    2: t('register.step.signin'),
    3: t('register.step.finish'),
  };
  // Per-step subtitle below the title — replaces the generic
  // "Pick a tag and you're in." text that previously sat there
  // unchanged across all three steps. Telling the user WHICH part of
  // the signup they're on makes the wizard feel intentional, not just
  // a long form chopped into pieces.
  const stepSubtitle: Record<Step, string> = {
    1: t('register.step.aboutSubtitle'),
    2: t('register.step.signinSubtitle'),
    3: t('register.step.finishSubtitle'),
  };

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-head">
          <div className="auth-head-badge">
            <Icon name="person_add" size={22} />
          </div>
          <h1 className="auth-h1">{t('register.title')}</h1>
          <p className="auth-sub">{stepSubtitle[step]}</p>
        </div>

        <Stepper step={step} labels={stepLabels} />

        <form
          onSubmit={(e) => {
            if (step < 3) { e.preventDefault(); next(); }
            else submit(e);
          }}
          className="space-y-3"
        >

          {/* ─── Step 1: About you ───────────────────────────── */}
          {step === 1 && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Field label={t('register.firstName')} error={showErr('first_name')}>
                  <div className="auth-field">
                    <Icon name="person" className="auth-field-icon" />
                    <input
                      className="auth-input"
                      value={form.first_name}
                      onChange={(e) => update('first_name', e.target.value.slice(0, 80))}
                      onBlur={() => setTouched((t) => ({ ...t, first_name: true }))}
                      placeholder={t('register.firstNamePh')}
                      autoComplete="given-name"
                      maxLength={80}
                      autoFocus
                    />
                  </div>
                </Field>
                <Field label={t('register.lastName')} error={showErr('last_name')}>
                  <div className="auth-field">
                    <Icon name="person" className="auth-field-icon" />
                    <input
                      className="auth-input"
                      value={form.last_name}
                      onChange={(e) => update('last_name', e.target.value.slice(0, 80))}
                      onBlur={() => setTouched((t) => ({ ...t, last_name: true }))}
                      placeholder={t('register.lastNamePh')}
                      autoComplete="family-name"
                      maxLength={80}
                    />
                  </div>
                </Field>
              </div>

              <Field
                label={t('register.dob')}
                hint={t('register.dobHelp')}
                error={showErr('birthdate')}
              >
                <div className="auth-field">
                  <Icon name="cake" className="auth-field-icon" />
                  <input
                    className="auth-input"
                    type="date"
                    value={form.birthdate}
                    onChange={(e) => update('birthdate', e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, birthdate: true }))}
                    min={DOB_MIN}
                    max={DOB_MAX}
                  />
                </div>
              </Field>

              <Field
                label={t('register.usernameLabel')}
                hint={t('register.usernameHelp')}
                hintRight={<AvailBadge state={avail.username.state} />}
                error={showErr('username')}
              >
                <div className="auth-field">
                  <Icon name="badge" className="auth-field-icon" />
                  <input
                    className="auth-input"
                    value={form.username}
                    onChange={(e) => update('username', e.target.value.replace(/[^a-zA-Z0-9_]/g, '').slice(0, 24))}
                    onBlur={(e) => {
                      setTouched((t) => ({ ...t, username: true }));
                      checkAvailability('username', e.target.value);
                    }}
                    placeholder={t('register.usernamePlaceholder')}
                    autoComplete="username"
                    maxLength={24}
                  />
                </div>
              </Field>
            </>
          )}

          {/* ─── Step 2: Sign-in ─────────────────────────────── */}
          {step === 2 && (
            <>
              <Field
                label={t('register.emailLabel')}
                optional
                hintRight={<AvailBadge state={avail.email.state} />}
                error={showErr('email')}
              >
                <div className="auth-field">
                  <Icon name="mail" className="auth-field-icon" />
                  <input
                    className="auth-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    onBlur={(e) => {
                      setTouched((t) => ({ ...t, email: true }));
                      checkAvailability('email', e.target.value);
                    }}
                    placeholder={t('register.emailPlaceholder')}
                    autoComplete="email"
                    maxLength={120}
                    autoFocus
                  />
                </div>
              </Field>

              <Field
                label={t('register.mobileLabel')}
                hint={t('register.mobileHelp')}
                hintRight={<AvailBadge state={avail.phone.state} />}
                error={showErr('phone')}
              >
                <div className="auth-field">
                  <Icon name="call" className="auth-field-icon" />
                  {/* +91 prefix badge. Sits between the call icon
                      (left .85rem) and the input value (pushed via
                      `paddingLeft: 4.6rem` inline). Inline style is
                      required because `.auth-field .auth-input` has
                      higher CSS specificity than Tailwind's
                      `pl-[4.6rem]` utility, so the utility class was
                      losing the cascade and the typed digits ran
                      visually under the +91 badge. */}
                  <span
                    className="absolute top-1/2 left-[2.6rem] -translate-y-1/2 text-sm font-bold
                               text-arena-700 dark:text-neon-200/85 pointer-events-none select-none"
                    aria-hidden
                  >
                    +91
                  </span>
                  <input
                    className="auth-input tabular-nums tracking-wider"
                    style={{ paddingLeft: '4.8rem' }}
                    value={form.phone}
                    onChange={(e) => update('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    onBlur={(e) => {
                      setTouched((t) => ({ ...t, phone: true }));
                      checkAvailability('phone', e.target.value);
                    }}
                    placeholder={t('register.mobilePlaceholder')}
                    inputMode="numeric"
                    pattern="[0-9]{10}"
                    maxLength={10}
                    autoComplete="tel-national"
                  />
                </div>
              </Field>

              <Field
                label={t('register.passwordLabel')}
                hint={t('register.passwordHelp')}
                error={showErr('password')}
              >
                <div className="auth-field">
                  <Icon name="lock" className="auth-field-icon" />
                  <input
                    className="auth-input"
                    type={showPwd ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => update('password', e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                    placeholder={t('register.passwordPlaceholder')}
                    autoComplete="new-password"
                    maxLength={72}
                  />
                  <button
                    type="button"
                    className="auth-field-trail"
                    aria-label={showPwd ? t('common.hide') : t('common.show')}
                    onClick={() => setShowPwd((v) => !v)}
                  >
                    <Icon name={showPwd ? 'visibility_off' : 'visibility'} />
                  </button>
                </div>
              </Field>

              <Field label={t('register.referralLabel')} optional error={showErr('referralCode')}>
                <div className="auth-field">
                  <Icon name="redeem" className="auth-field-icon" />
                  <input
                    className="auth-input uppercase tracking-wider font-mono"
                    value={form.referralCode}
                    onChange={(e) => update('referralCode', e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 20).toUpperCase())}
                    onBlur={() => setTouched((t) => ({ ...t, referralCode: true }))}
                    placeholder={t('register.referralPlaceholder')}
                    maxLength={20}
                  />
                </div>
              </Field>
            </>
          )}

          {/* ─── Step 3: Location ────────────────────────────── */}
          {step === 3 && (
            <>
              <div className="rounded-xl border border-brand-200/70 dark:border-brand-400/30
                              bg-brand-50/70 dark:bg-brand-500/10 p-3
                              flex items-start gap-2">
                <Icon name="my_location" size={18} filled
                      className="text-brand-700 dark:text-brand-300 shrink-0 mt-0.5" />
                <div className="text-[12.5px] leading-snug text-brand-900 dark:text-brand-100">
                  <div className="font-bold">{t('register.geo.title')}</div>
                  <div className="opacity-85 mt-0.5">{t('register.geo.subtitle')}</div>
                </div>
              </div>

              {/* GPS-first layout. LocationCapture's `gpsFirstOnly`
                  prop hides every section except the "Use my current
                  location" button. Flipping `showAltGeo` re-mounts
                  the component with full alternatives (pincode +
                  state/district/area cascade) visible. Any captured
                  geo — GPS, pincode, or area — is enough to finish
                  signup; see hasAnyGeo above.
                  `hidePurposeBanner` tells LocationCapture not to
                  render its own amber explainer + "Now lock your
                  GPS" preamble in either mode, because the orange
                  "Where are you playing from?" card above already
                  frames the same idea — keeping both was the main
                  source of the vertical clutter on mobile. */}
              <LocationCapture
                value={geo}
                onChange={setGeo}
                mode="self"
                showSavedSummary={false}
                required={false}
                disabled={loading}
                gpsFirstOnly={!showAltGeo}
                hidePurposeBanner
                // GPS refused / failed → auto-reveal the pincode +
                // state/district/area fallback so the player is never
                // stuck on a dead GPS button.
                onGpsDenied={() => setShowAltGeo(true)}
              />

              {/* Tiny secondary affordance: text + chevron that toggles
                  the alt-methods panel visible. Pinpoint the player at
                  GPS first, give them an explicit out if they refuse. */}
              <button
                type="button"
                onClick={() => setShowAltGeo((v) => !v)}
                className="w-full text-center text-[11.5px] font-semibold
                           text-arena-600 dark:text-neon-200/70
                           hover:text-arena-800 dark:hover:text-neon-100
                           underline-offset-2 hover:underline transition py-1"
              >
                {showAltGeo
                  ? <>{t('register.geo.hideAlt')} <Icon name="expand_less" size={12} className="inline-block align-middle" /></>
                  : <>{t('register.geo.showAlt')} <Icon name="expand_more" size={12} className="inline-block align-middle" /></>}
              </button>

              {/* Saved-summary chip — confirms the captured location
                  before the player submits. Shows whichever signal
                  they provided (GPS lock, pincode, area). */}
              {geoOk && (
                <div className="rounded-xl border border-emerald-300 dark:border-emerald-400/40
                                bg-emerald-50/80 dark:bg-emerald-500/10 px-3 py-2.5
                                flex items-start gap-2">
                  <Icon name="check_circle" size={16} filled
                        className="text-emerald-700 dark:text-emerald-300 shrink-0 mt-0.5" />
                  <div className="text-[12px] text-emerald-900 dark:text-emerald-100 leading-snug">
                    <div className="font-bold">{t('register.geo.captured')}</div>
                    <div className="opacity-85">{t('register.geo.capturedHint')}</div>
                  </div>
                </div>
              )}

              <div className="rounded-xl border border-arena-200/70 dark:border-arena-600/40
                              bg-arena-50/60 dark:bg-arena-900/30 p-3
                              flex items-start gap-2 mt-2">
                <Icon name="info" size={16} className="text-arena-500 dark:text-neon-200/70 shrink-0 mt-0.5" />
                <div className="text-[11.5px] text-arena-700 dark:text-neon-200/80 leading-snug">
                  {t('register.geo.verifyNote')}
                </div>
              </div>
            </>
          )}

          {error && <div className="auth-error" role="alert">{error}</div>}

          {/* ─── Step nav ───────────────────────────────────── */}
          {/* `min-w-0` on the row + `flex-1 min-w-0` on the submit
              button is the critical pair: without min-w-0 a flex
              child whose CONTENT is naturally wider than the flex
              parent (uppercase + letter-spacing CTAs are exactly
              that) refuses to shrink and pushes the parent out, even
              with overflow-hidden upstream. The min-w-0 lets the
              button shrink to whatever space is left after the Back
              pill, so the CTA always fits inside the auth-card edge
              on narrow phones. The Back pill itself gets shrink-0
              so it stays at its natural label width. */}
          <div className="flex items-center gap-2 pt-1 min-w-0">
            {step > 1 && (
              <button
                type="button"
                onClick={back}
                disabled={loading}
                className="shrink-0 inline-flex items-center justify-center gap-1 px-4 py-2.5 rounded-full text-sm font-bold
                           text-arena-700 dark:text-neon-200
                           bg-arena-100 dark:bg-arena-700/60
                           hover:bg-arena-200 dark:hover:bg-arena-700
                           disabled:opacity-50"
              >
                <Icon name="arrow_back" size={16} /> {t('common.back')}
              </button>
            )}
            {step < 3 ? (
              <button
                type="submit"
                disabled={!stepValid[step] || loading}
                className="auth-cta flex-1 min-w-0 whitespace-nowrap"
                style={{ paddingLeft: '1rem', paddingRight: '1rem', letterSpacing: '.06em' }}
              >
                <span className="inline-flex items-center justify-center gap-2">
                  {t('common.next')} <Icon name="arrow_forward" />
                </span>
              </button>
            ) : (
              <button
                type="submit"
                disabled={!stepValid[3] || loading}
                className="auth-cta flex-1 min-w-0 whitespace-nowrap overflow-hidden"
                style={{ paddingLeft: '1rem', paddingRight: '1rem', letterSpacing: '.05em' }}
              >
                <span className="inline-flex items-center justify-center gap-2">
                  {loading ? (
                    <>
                      <Icon name="progress_activity" className="animate-spin" />
                      <span className="truncate">{t('register.creating')}</span>
                    </>
                  ) : (
                    <>
                      <span className="truncate">{t('register.cta')}</span>
                      <Icon name="check" />
                    </>
                  )}
                </span>
              </button>
            )}
          </div>
        </form>

        <div className="auth-foot">
          {t('register.alreadyPlayer')} <Link to="/login" className="auth-link">{t('common.signIn')}</Link>
        </div>
      </div>
    </div>
  );
}
